// Regras compartilhadas do Assistente IA (plano de aula).

// O assistente ainda está liberado só para as escolas de teste.
const ESCOLAS_COM_ASSISTENTE_IA = [792, 804, 789, 205];

export function escolaTemAssistenteIa(usuario) {
  if (!usuario) return false;
  const escolaId = usuario.id_escola || usuario.escola_id || usuario.escola?.id;
  return ESCOLAS_COM_ASSISTENTE_IA.includes(parseInt(escolaId));
}

// Mesmas faixas das cores dos relatórios (getNivel).
export function nivelDesempenho(porcentagem) {
  if (porcentagem >= 67) return "Satisfatório";
  if (porcentagem >= 33) return "Parcialmente satisfatório";
  return "Insatisfatório";
}

// Mesmo formato do campo "nome" das habilidades do formulário
// (/ai-assistent/get-skills), para o campo já vir selecionado.
export function nomeHabilidade({ codigo, descricao }) {
  return `(${codigo}) ${descricao}`;
}

function linhaHabilidade(habilidade) {
  let linha = `- ${nomeHabilidade(habilidade)}: ${habilidade.porcentagem}% de acerto (${nivelDesempenho(
    habilidade.porcentagem
  )})`;

  if (habilidade.distribuicao) {
    const { insatisfatorio, parcial, satisfatorio } = habilidade.distribuicao;
    linha += `; estudantes por nível: ${insatisfatorio}% Insatisfatório, ${parcial}% Parcial, ${satisfatorio}% Satisfatório`;
  }

  return linha;
}

/**
 * Monta a rota do gerador de plano de aula já preenchida com o desempenho da
 * turma. `habilidades` vem em ordem de prioridade: a primeira vira a
 * habilidade do formulário e todas entram no contexto enviado à IA.
 */
export function rotaPlanoAulaPorDesempenho({ avaliacao, turma, habilidades }) {
  const [principal] = habilidades;
  const turmaNome = turma && turma.nome ? ` — turma ${turma.nome}` : "";

  const desempenho = [
    `Avaliação "${avaliacao.titulo}"${turmaNome}.`,
    habilidades.length > 1
      ? "Habilidades com menor desempenho da turma:"
      : "Habilidade trabalhada:",
    ...habilidades.map(linhaHabilidade),
  ].join("\n");

  const query = {
    // O gerador abre em outra aba; com essa origem, o "fechar" fecha a aba
    // e o professor volta ao relatório em vez de ir para a home.
    origem: "relatorio",
    id_disciplina: avaliacao.id_disciplina,
    id_ano: avaliacao.id_ano,
    habilidade: nomeHabilidade(principal),
    objetivo: `Retomar a habilidade ${principal.codigo}, em que a turma teve ${principal.porcentagem}% de acerto na avaliação.`,
    desempenho,
  };

  if (principal.conteudo) {
    query.assunto = principal.conteudo;
  }

  return { path: "/professor/assistente-ia/planos", query };
}

// Abre o gerador já preenchido em nova aba, mantendo o relatório aberto.
export function abrirPlanoAulaEmNovaAba(router, rota) {
  window.open(router.resolve(rota).href, "_blank");
}

export class StreamingIndisponivel extends Error {}

function lerEvento(bloco) {
  let evento = "message";
  const dados = [];

  bloco.split("\n").forEach((linha) => {
    if (linha.startsWith("event:")) evento = linha.slice(6).trim();
    if (linha.startsWith("data:")) dados.push(linha.slice(5).trim());
  });

  return { evento, dados: dados.length ? JSON.parse(dados.join("\n")) : {} };
}

// Enquanto escreve, o modelo às vezes abre com ```html; o HTML final vem limpo.
function semCercaMarkdown(texto) {
  return texto.replace(/^\s*```(html)?\s*/i, "").replace(/```\s*$/, "");
}

/**
 * Gera o plano em streaming (/lesson-plan/generate-stream). Chama
 * `aoReceberTexto` com o HTML parcial acumulado e devolve o HTML final.
 * Lança StreamingIndisponivel se o servidor ainda não tiver o endpoint.
 */
export async function gerarPlanoAulaEmStreaming(dados, aoReceberTexto) {
  let resposta;
  try {
    resposta = await fetch(
      `${process.env.VUE_APP_API_ASSISTENTE_IA_URL}/lesson-plan/generate-stream`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: "Bearer " + window.localStorage.getItem("token"),
        },
        body: JSON.stringify(dados),
      }
    );
  } catch (erroDeRede) {
    // Falha antes de qualquer resposta (rede/CORS): tenta a geração tradicional.
    throw new StreamingIndisponivel(erroDeRede.message);
  }

  if (resposta.status === 401) {
    window.localStorage.removeItem("token");
    window.location.href = "/";
    throw new Error("Sessão expirada.");
  }

  const contentType = resposta.headers.get("Content-Type") || "";
  if (!resposta.ok || !resposta.body || !contentType.includes("text/event-stream")) {
    throw new StreamingIndisponivel(`Streaming indisponível (HTTP ${resposta.status})`);
  }

  const leitor = resposta.body.getReader();
  const decodificador = new TextDecoder();
  let pendente = "";
  let texto = "";

  for (;;) {
    const { done, value } = await leitor.read();
    if (done) break;

    pendente += decodificador.decode(value, { stream: true });

    let fim;
    while ((fim = pendente.indexOf("\n\n")) >= 0) {
      const { evento, dados: carga } = lerEvento(pendente.slice(0, fim));
      pendente = pendente.slice(fim + 2);

      if (evento === "chunk") {
        texto += carga.text;
        aoReceberTexto(semCercaMarkdown(texto));
      } else if (evento === "done") {
        return carga.plan;
      } else if (evento === "error") {
        throw new Error(carga.message);
      }
    }
  }

  throw new Error("A geração do plano foi interrompida antes do fim.");
}
